import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Modal } from "react-bootstrap";
import { HeartFill, XCircle, Dash } from "react-bootstrap-icons";
import { useDispatch } from "react-redux";
import { clearActiveExercise } from "../../../redux/slices/exerciseSlice";
import { getRandomSentence } from "../../../utils/getRandomSentence";
import "./InsertSpaces.css";

export default function InsertSpaces() {
  const [originalSentence, setOriginalSentence] = useState(""); // Original sentence for reference
  const [exerciseSentence, setExerciseSentence] = useState(""); // Cleaned sentence without spaces or punctuation
  const [cursorPosition, setCursorPosition] = useState(0); // Cursor position in the exercise sentence
  const [leftPart, setLeftPart] = useState(""); // Left part of the sentence
  const [rightPart, setRightPart] = useState(""); // Right part of the sentence
  const [remainingAttempts, setRemainingAttempts] = useState(3); // Tracks remaining attempts
  const [showErrorModal, setShowErrorModal] = useState(false); // Error modal for incorrect insertion
  const [showNoAttemptsModal, setShowNoAttemptsModal] = useState(false); // "No Attempts Left" modal
  const [showExerciseFinishedModal, setShowExerciseFinishedModal] =
    useState(false); // "Exercise Finished" modal
  const [lastSpacePosition, setLastSpacePosition] = useState(-1); // Track the last inserted space position
  const [correctSpaceCount, setCorrectSpaceCount] = useState(0); // Count of correct spaces inserted
  const [originalSpaceCount, setOriginalSpaceCount] = useState(0); // Total spaces in original sentence
  const dispatch = useDispatch();

  // Fetch a random sentence from the database
  const fetchSentence = async () => {
    try {
      const fetchedSentence = await getRandomSentence();
      setOriginalSentence(fetchedSentence.sentence); // Store original sentence as reference

      // Create a cleaned version of the sentence without spaces or punctuation
      const cleanedSentence = fetchedSentence.sentence.replace(
        /[.,\s!?;:()]/g,
        ""
      );
      setExerciseSentence(cleanedSentence); // Store cleaned sentence for the exercise
      setRightPart(cleanedSentence); // Initialize right part with the cleaned sentence
      setCursorPosition(0); // Reset cursor position
      setRemainingAttempts(3); // Reset attempts
      setLeftPart(""); // Reset left part
      setShowNoAttemptsModal(false); // Reset modals
      setShowErrorModal(false);
      setShowExerciseFinishedModal(false);
      setLastSpacePosition(-1); // Reset last space position
      setCorrectSpaceCount(0); // Reset correct space count

      // Count spaces in the original sentence
      const totalSpaces = (fetchedSentence.sentence.match(/\s/g) || []).length;
      setOriginalSpaceCount(totalSpaces); // Store total space count in original sentence
    } catch (error) {
      console.error("Failed to fetch sentence:", error);
    }
  };

  // Fetch sentence on component mount
  useEffect(() => {
    fetchSentence();
  }, []);

  // Update left and right parts when the cursor position changes
  useEffect(() => {
    setLeftPart(exerciseSentence.slice(0, cursorPosition));
    setRightPart(exerciseSentence.slice(cursorPosition));
  }, [cursorPosition, exerciseSentence]);

  // Add a space to the exerciseSentence at the current cursor position
  const addSpaceToExerciseSentence = () => {
    const nextOriginalChar = originalSentence[cursorPosition];
    const followingChar = originalSentence[cursorPosition + 1];
    const lastInsertedChar = exerciseSentence[cursorPosition - 1];

    // Check if the last character inserted is already a space at the same position
    if (lastInsertedChar === " " && cursorPosition > 0) {
      console.log("Space already inserted at this position; ignoring.");
      return;
    }

    let newLeftPart = exerciseSentence.slice(0, cursorPosition);
    let newExerciseSentence = exerciseSentence;
    let isCorrectInsertion = false;

    // Check if the next character in the original sentence is punctuation followed by a space
    if (/[.,!?;:()]/.test(nextOriginalChar) && followingChar === " ") {
      // Append punctuation and space together
      newLeftPart += nextOriginalChar + " ";
      newExerciseSentence =
        exerciseSentence.slice(0, cursorPosition) +
        nextOriginalChar +
        " " +
        exerciseSentence.slice(cursorPosition);
      setCursorPosition(cursorPosition + 2); // Move cursor past punctuation and space
      isCorrectInsertion = true;
    } else {
      // Regular space insertion
      newLeftPart += " ";
      isCorrectInsertion = checkSpaceInsertion(newLeftPart);

      if (isCorrectInsertion) {
        newExerciseSentence =
          exerciseSentence.slice(0, cursorPosition) +
          " " +
          exerciseSentence.slice(cursorPosition);
        setCursorPosition(cursorPosition + 1); // Move cursor right after inserting space
      }
    }

    // Only update state if insertion is correct
    if (isCorrectInsertion) {
      setExerciseSentence(newExerciseSentence);
      setLastSpacePosition(cursorPosition); // Update last space position
      setCorrectSpaceCount((prevCount) => prevCount + 1); // Increment correct space count

      // Check if the exercise is complete
      if (correctSpaceCount + 1 === originalSpaceCount) {
        setShowExerciseFinishedModal(true); // Show exercise finished modal
      }
    } else {
      handleIncorrectInsertion(); // Show error modal without updating state
    }
  };

  // Function to check the correctness of space insertion with detailed logging
  const checkSpaceInsertion = (newLeftPart) => {
    const comparisonPart = originalSentence.slice(0, newLeftPart.length);

    if (newLeftPart === comparisonPart) {
      return true;
    } else {
      return false;
    }
  };

  // Handle incorrect insertion with attempts management
  const handleIncorrectInsertion = () => {
    setRemainingAttempts((prev) => {
      const updatedAttempts = prev - 1;
      if (updatedAttempts <= 0) {
        setShowNoAttemptsModal(true);
      } else {
        setShowErrorModal(true);
        setTimeout(() => setShowErrorModal(false), 1000);
      }
      return updatedAttempts;
    });
  };

  // Handle left button click to move the cursor left, restricted to the last inserted space
  const handleMoveLeft = () => {
    if (cursorPosition > 0 && cursorPosition > lastSpacePosition + 1) {
      setCursorPosition(cursorPosition - 1);
    }
  };

  // Handle right button click to move the cursor right
  const handleMoveRight = () => {
    if (cursorPosition < exerciseSentence.length) {
      setCursorPosition(cursorPosition + 1);
    }
  };

  // Handle keyboard events for left/right arrows and space
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft") {
        handleMoveLeft();
      } else if (event.key === "ArrowRight") {
        handleMoveRight();
      } else if (event.key === " ") {
        event.preventDefault(); // Prevent scrolling when pressing space
        addSpaceToExerciseSentence();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [cursorPosition, exerciseSentence]);

  // Restart the exercise with a new sentence
  const restartExercise = () => {
    setShowNoAttemptsModal(false);
    setShowExerciseFinishedModal(false);
    fetchSentence();
  };

  // Render the error modal
  const renderErrorModal = () => (
    <Modal
      show={showErrorModal}
      onHide={() => setShowErrorModal(false)}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton={false}>
        <Modal.Title>Incorrect Space Insertion</Modal.Title>
      </Modal.Header>
      <Modal.Body>Please try again.</Modal.Body>
    </Modal>
  );

  // Render the "No attempts left" modal
  const renderNoAttemptsModal = () => (
    <Modal
      show={showNoAttemptsModal}
      onHide={() => setShowNoAttemptsModal(false)}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header>
        <Modal.Title>No Attempts Left</Modal.Title>
      </Modal.Header>
      <Modal.Body>Would you like to restart the exercise?</Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={restartExercise}>
          Restart Exercise
        </Button>
        <Button
          variant="danger"
          onClick={() => dispatch(clearActiveExercise())}
        >
          Exit
        </Button>
      </Modal.Footer>
    </Modal>
  );

  // Render the exercise finished modal
  const renderExerciseFinishedModal = () => (
    <Modal
      show={showExerciseFinishedModal}
      onHide={() => setShowExerciseFinishedModal(false)}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header>
        <Modal.Title>Exercise Finished</Modal.Title>
      </Modal.Header>
      <Modal.Body>Would you like to do one more exercise?</Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={restartExercise}>
          Yes
        </Button>
        <Button
          variant="danger"
          onClick={() => dispatch(clearActiveExercise())}
        >
          Exit
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <>
      {/* Attempts indicator */}
      <div className="attempts-header d-flex justify-content-between pt-2">
        <div className="attempts-indicator">
          {Array.from({ length: remainingAttempts }).map((_, idx) => (
            <HeartFill key={idx} className="heart-icon" />
          ))}
        </div>
        <div>
          <XCircle
            className="x-icon"
            onClick={() => dispatch(clearActiveExercise())}
          />
        </div>
      </div>

      <Container fluid className="build-sentence-container">
        {/* Reference Sentence */}
        {/* <Row className="justify-content-center align-items-center reference-row">
          <Col xs={12} md={8} lg={6} className="text-center">
            <h3 className="mb-4">Original Sentence</h3>
            <div className="reference-sentence">{originalSentence}</div>
          </Col>
        </Row> */}

        {/* Main Content */}
        <Row className="justify-content-center align-items-center sentence-row">
          <Col xs={12} md={8} lg={6} className="text-center">
            <h3 className="mb-4">Insert Spaces</h3>
            <div className="sentence-wrapper">
              <div className="sentence-left">{leftPart}</div>
              <div className="blinking-cursor"></div>
              <div className="sentence-right">{rightPart}</div>
            </div>
          </Col>
        </Row>

        {/* Buttons Row */}
        <Row className="justify-content-center align-items-center button-row">
          <Col xs={12} md={8} lg={6} className="text-center">
            <div className="exercise-buttons">
              <Button
                className="prev-button"
                variant="light"
                onClick={handleMoveLeft}
              >
                &#8249;
              </Button>
              <Button
                className="counter-button"
                variant="light"
                onClick={addSpaceToExerciseSentence}
              >
                <Dash />
              </Button>
              <Button
                className="next-button"
                variant="light"
                onClick={handleMoveRight}
              >
                &#8250;
              </Button>
            </div>
          </Col>
        </Row>

        {/* Modals */}
        {renderErrorModal()}
        {renderNoAttemptsModal()}
        {renderExerciseFinishedModal()}
      </Container>
    </>
  );
}
